import { FormInstance, message } from 'antd';
import { PackingTypeSale } from 'enums/packingTypeSale';
import produce, { Draft } from 'immer';
import { isEmpty } from 'lodash';
import Packing from 'model/Packing';
import { IQualityObservation } from 'model/QualityObservation';
import { Moment } from 'moment';
import { getGfpOffers } from 'services/gfpOfferService';
import { getQualityObservations } from 'services/qualityObservationService';
import { IGfpOffer, IGfpOfferParams } from 'types/api/GfpOffer';
import { create } from 'zustand';

export type IGfpOfferFormFilter = {
  deliveryDate: Moment | undefined;
  observationIds: number[];
  keyword: string | undefined;
};

export type IGfpOfferFilters = {
  observations: IQualityObservation[];
  isObservationLoading: boolean;
  isObservationSuccessful: boolean;
  isObservationError: boolean;
};

export type IGfpOfferTable = {
  data: IGfpOffer[];
  isError: boolean;
};

export type IGfpOfferStore = {
  table: IGfpOfferTable;
  filters: IGfpOfferFilters;
  formFilterInstance: Partial<FormInstance<IGfpOfferFormFilter>> | null;
  packingSelected: Packing;
  packingTypeSelected: PackingTypeSale;
  setData: (fn: (state: Draft<IGfpOfferStore>) => void) => void;
  setFormInstance: (form: Partial<FormInstance<IGfpOfferFormFilter>>) => void;
  resetFilters: () => void;
  resetStatesToDefault: () => void;
  getQualityObservations: () => Promise<IQualityObservation[]>;
  loadFilters: () => void;
  getParams(): IGfpOfferParams;
  setPackingSelected: (packing: Packing) => void;
  setPackingTypeSelected: (packingType: PackingTypeSale) => void;
  handleSearch: (
    lkpOfferId: number,
    offerType: number,
    packingId?: string,
    auctionDate?: string,
  ) => Promise<void>;
};

export const initialTableState: IGfpOfferTable = {
  data: [],
  isError: false,
};

export const initialFiltersState: IGfpOfferFilters = {
  observations: [],
  isObservationLoading: false,
  isObservationSuccessful: false,
  isObservationError: false,
};

export const initialPackingSelectedState: Packing = {
  abbrev: '',
  code: '',
  creationDate: '',
  description: '',
  id: 0,
  layersByTrolley: 0,
  isDeleted: false,
  additionalCost: false,
  modificationDate: '',
  multiple: 0,
  minimumQuantity: 0,
  type: '',
  volOfferId: 0,
  maxAvailablePackingsLabel: '',
  maxAvailableLayersLabel: '',
  maxAvailableTrolleysLabel: '',
  maxAvailablePackingsQuantity: 0,
  maxAvailableLayersQuantity: 0,
  maxAvailableTrolleysQuantity: 0,
  packagingByLayer: 0,
  unitsPerPackage: 0,
};

const gfpOfferStore = create<IGfpOfferStore>()((set, get) => ({
  table: initialTableState,
  filters: initialFiltersState,
  formFilterInstance: null,
  packingSelected: initialPackingSelectedState,
  packingTypeSelected: PackingTypeSale.Packing,

  setData: (fn) => set(produce(fn)),
  setFormInstance: (form: Partial<FormInstance<IGfpOfferFormFilter>>) =>
    set({ formFilterInstance: form }),
  resetFilters: () => set({ filters: initialFiltersState }),
  resetStatesToDefault: () => {
    get().setData((state) => {
      state.filters = initialFiltersState;
      state.formFilterInstance = null;
      state.table = initialTableState;
    });
  },

  // Getters
  getParams: () => {
    const formValues = get().formFilterInstance?.getFieldsValue?.();

    return {
      deliveryDate: formValues?.deliveryDate?.format('YYYY-MM-DD'),
      qualityObservationId: formValues?.observationIds,
      keyword: isEmpty(formValues?.keyword) ? undefined : formValues?.keyword,
    } as IGfpOfferParams;
  },

  getQualityObservations: async () => {
    get().setData((state) => {
      state.filters.isObservationLoading = true;
      state.filters.isObservationSuccessful = false;
      state.filters.isObservationError = false;
    });
    try {
      const { data } = await getQualityObservations();

      get().setData((state) => {
        state.filters.observations = data?.result || [];
        state.filters.isObservationLoading = false;
        state.filters.isObservationSuccessful = true;
        state.filters.isObservationError = false;
      });
      return data?.result || [];
    } catch {
      get().setData((state) => {
        state.filters.observations = [];
        state.filters.isObservationLoading = false;
        state.filters.isObservationSuccessful = false;
        state.filters.isObservationError = true;
      });
      return [];
    }
  },

  loadFilters: () => {
    get().getQualityObservations();
  },

  handleSearch: async (
    lkpOfferId: number,
    offerType: number,
    packingId?: string,
    auctionDate?: string,
  ) => {
    const { setData, getParams } = get();

    try {
      setData((state) => {
        state.table.isError = false;
      });

      const params = { ...getParams(), offerType, packingId, auctionDate };

      const response = await getGfpOffers(params, lkpOfferId);

      const { data } = response;

      setData((state) => {
        state.table.data = data || [];
        state.table.isError = false;
      });

      return Promise.resolve();
    } catch (error) {
      setData((state) => {
        state.table.data = [];
        state.table.isError = true;
      });

      message.error('Não foi possível carregar a tabela.');

      return Promise.reject();
    }
  },
  setPackingSelected: (packing: Packing) => {
    get().setData((state) => {
      state.packingSelected = packing;
    });
  },
  setPackingTypeSelected: (packingType: PackingTypeSale) => {
    get().setData((state) => {
      state.packingTypeSelected = packingType;
    });
  },
}));

export default gfpOfferStore;
